import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useProject } from '../../hooks/useProject';
import { useUpdateProject } from '../../hooks/useUpdateProject';
import { Input, DateInput, Textarea, Select, ErrorPage, Paragraph, LoadingButton } from '../../../../components/shared';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function EditProject() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { project, loading, error } = useProject(id!);
  const { updateProject, loading: updateLoading, error: updateError } = useUpdateProject();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    startDate: '',
    endDate: '',
    paymentMethod: '',
    hasConstructionTracking: false,
    hasAdhocTracking: false,
    internalNotes: '',
    status: '',
  });

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        address: project.address || '',
        startDate: (project.startDate as string | null) || '',
        endDate: (project.endDate as string | null) || '',
        paymentMethod: project.paymentMethod || '',
        hasConstructionTracking: project.hasConstructionTracking || false,
        hasAdhocTracking: project.hasAdhocTracking || false,
        internalNotes: project.internalNotes || '',
        status: project.status || '',
      });
    }
  }, [project]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Form data status:', formData.status);
      // For update, only send fields that have changed or are not empty
      const updateData: any = { id: id! };

      // Always include required fields
      updateData.name = formData.name;
      updateData.paymentMethod = formData.paymentMethod;

      // Always include status since it's an enum
      updateData.status = formData.status;

      // Include other fields only if they have values
      if (formData.address) updateData.address = formData.address;
      if (formData.hasConstructionTracking !== undefined) updateData.hasConstructionTracking = formData.hasConstructionTracking;
      if (formData.hasAdhocTracking !== undefined) updateData.hasAdhocTracking = formData.hasAdhocTracking;

      // Handle dates - only include if they have values
      if (formData.startDate) updateData.startDate = formData.startDate;
      if (formData.endDate) updateData.endDate = formData.endDate;

      // Handle internal notes - include even if empty string to allow clearing
      if (formData.internalNotes !== undefined) updateData.internalNotes = formData.internalNotes;

      console.log('Update data being sent:', updateData);

      await updateProject(updateData);
      navigate(`/projects/${id}`);
    } catch (err) {
      console.error('Failed to update project:', err);
    }
  };

  const canEdit = currentUser?.role === 'ADMIN' && project?.members?.some((m: any) => m.id === currentUser.id);

  if (error) return <ErrorPage message={`Erro: ${error.message}`} />;
  if (!project && !loading) return <ErrorPage message="Projeto não encontrado" />;
  if (!canEdit && !loading) return <ErrorPage message="Você não tem permissão para editar este projeto" />;

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
      <div className="container-elegant max-w-4xl">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <Link
              to={`/projects/${id}`}
              className="text-sm uppercase tracking-widest text-primary-600 dark:text-primary-400 hover:text-primary-950 dark:hover:text-white mb-4"
            >
              ← Voltar ao Projeto
            </Link>
            <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white mb-2">
              Editar Projeto
            </h1>
            <Paragraph>
              {project?.name}
            </Paragraph>
          </div>
        </div>

        {updateError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>Erro ao atualizar projeto: {updateError.message}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleUpdate} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Input
                label="Nome do Projeto *"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                variant="primary"
                loading={loading}
                required
              />

              <Textarea
                label="Endereço"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={3}
                variant="primary"
                loading={loading}
              />

              <Input
                label="Método de Pagamento"
                type="text"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                variant="primary"
                loading={loading}
              />

              <div className="grid grid-cols-2 gap-4">
                <DateInput
                  label="Data de Início"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  variant="primary"
                  loading={loading}
                />

                <DateInput
                  label="Data de Término"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  variant="primary"
                  loading={loading}
                />
              </div>

              <Select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                variant="primary"
                loading={loading}
              >
                <option value="PLANNING">Planejamento</option>
                <option value="IN_PROGRESS">Em Andamento</option>
                <option value="COMPLETED">Concluído</option>
                <option value="ON_HOLD">Em Espera</option>
                <option value="CANCELLED">Cancelado</option>
              </Select>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch
                    id="hasConstructionTracking"
                    checked={formData.hasConstructionTracking}
                    onCheckedChange={(checked) => setFormData({ ...formData, hasConstructionTracking: checked })}
                  />
                  <Label htmlFor="hasConstructionTracking" className="text-sm text-primary-900 dark:text-white cursor-pointer">
                    Acompanhamento de Construção (Diário de Obra)
                  </Label>
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    id="hasAdhocTracking"
                    checked={formData.hasAdhocTracking}
                    onCheckedChange={(checked) => setFormData({ ...formData, hasAdhocTracking: checked })}
                  />
                  <Label htmlFor="hasAdhocTracking" className="text-sm text-primary-900 dark:text-white cursor-pointer">
                    Acompanhamento Ad-hoc
                  </Label>
                </div>
              </div>

              {currentUser?.role === 'ADMIN' && (
                <Textarea
                  label="Notas Internas"
                  value={formData.internalNotes}
                  onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                  rows={4}
                  placeholder="Notas visíveis apenas para administradores"
                  variant="primary"
                  loading={loading}
                />
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <LoadingButton
              type="submit"
              loading={updateLoading}
              loadingText="ATUALIZANDO..."
              className="bg-primary-950 dark:bg-white text-white dark:text-primary-950 px-8 py-3 text-sm uppercase tracking-widest font-light hover:bg-primary-800 dark:hover:bg-primary-100 transition-colors duration-300"
            >
              ATUALIZAR PROJETO
            </LoadingButton>
            <Button asChild variant="outline">
              <Link to={`/projects/${id}`}>CANCELAR</Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}